/**
 * The Upcoming Work date cell: the visit date, an icon badge when the attendance moved, and a warning icon
 * badge when stored work sits on a day blacked out after it was stored. Both keep their words in an
 * accessible tooltip because the date column is narrow.
 */
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { UpcomingWork } from '@jms/model';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { DateTime } from 'luxon';
import { OCCURRENCE_BLACKOUT_LABEL } from '../../../work.task.schedule/presentation/occurrence.blackout.tag';
import { upcomingWorkVisitDateBridgeStyles, upcomingWorkVisitDateStyleId } from './upcoming.work.visit.date.theme';

@Component({
  selector: 'wb-upcoming-work-visit-date',
  imports: [BadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span>{{ $date() }}</span>
    @if ($originalDate()) {
      <w-badge size="xs" iconLeft="fa-regular fa-calendar-days" role="img" [attr.aria-label]="$tooltip()" [tooltip]="$tooltip()"
        color="var(--local-upcoming-work-visit-date-text)"
        backgroundColor="var(--local-upcoming-work-visit-date-background)"
        borderColor="var(--local-upcoming-work-visit-date-border)" />
    }
    @if ($onBlackout()) {
      <w-badge size="xs" iconLeft="fa-regular fa-calendar-xmark" role="img" [attr.aria-label]="blackoutLabel" [tooltip]="blackoutLabel"
        color="var(--local-upcoming-work-visit-date-blackout-text)"
        backgroundColor="var(--local-upcoming-work-visit-date-blackout-background)"
        borderColor="var(--local-upcoming-work-visit-date-blackout-border)" />
    }
  `,
  styleUrl: './upcoming.work.visit.date.cell.renderer.less',
})
export class UpcomingWorkVisitDateCellRenderer implements ICellRendererAngularComp {
  private readonly document = inject(DOCUMENT);
  protected readonly $date = signal('');
  protected readonly $originalDate = signal('');
  protected readonly $tooltip = computed(() => `Rescheduled from ${this.$originalDate()}. Regular schedule unchanged.`);
  protected readonly $onBlackout = signal(false);
  protected readonly blackoutLabel = OCCURRENCE_BLACKOUT_LABEL;

  public constructor() {
    DesignSystem.Mount(this.document, upcomingWorkVisitDateStyleId, upcomingWorkVisitDateBridgeStyles);
  }

  public agInit(params: ICellRendererParams<UpcomingWork>): void {
    const date = DateTime.fromISO(params.data?.visitDate ?? '', { zone: 'utc' });
    const original = DateTime.fromISO(params.data?.originalVisitDate ?? '', { zone: 'utc' });
    this.$date.set(params.valueFormatted ?? (date.isValid ? date.toFormat('dd/MM/yyyy') : ''));
    this.$originalDate.set(original.isValid && original.toISODate() !== date.toISODate() ? original.toFormat('d LLL yyyy') : '');
    this.$onBlackout.set(params.data?.onBlackout === true);
  }

  public refresh(params: ICellRendererParams<UpcomingWork>): boolean {
    this.agInit(params);
    return true;
  }
}
